import chroma from 'chroma-js';
import { CssColorSpace } from './colorCssTypes';

/**
 * Determine the colour space of a given colour string.
 */
export const getColorSpace = (cssColor: string): CssColorSpace => {
    if (!cssColor || cssColor.startsWith('#')) return 'hex';
    if (cssColor.startsWith('rgb')) return 'rgb';
    if (cssColor.startsWith('hsl')) return 'hsl';
    if (cssColor.startsWith('lab')) return 'lab';
    if (cssColor.startsWith('oklab')) return 'oklab';
    if (cssColor.startsWith('lch')) return 'lch';
    if (cssColor.startsWith('oklch')) return 'oklch';
    return 'hex';
};

/**
 * Get the colour to initialise the colour input with,
 * but uses the fallback colour if the provided colour is invalid.
 *
 * NOTE: For this function to be "safe" the fallback colour should be a valid CSS colour.
 */
export const getChromaColorSafe = (cssColor: string, fallbackColor: string = 'black'): chroma.Color => {
    try {
        return chroma(cssColor);
    } catch (error) {
        // Fallback to black if the colour is invalid
        return chroma(fallbackColor);
    }
};

/**
 * Strips unnecessary characters from a CSS colour string to simplify it.
 */
const simplifiedColorString = (color: string): string =>
    color
        // Remove all "deg" symbols
        .replace(/deg/g, '')
        // Remove all percentage symbols
        .replace(/%/g, '')
        // Replace all decimal values that are not alphas (after "/ ")
        .replace(/(?<!\/\s)\b\d+\.\d+/g, (match) => String(Math.round(parseFloat(match))));

/**
 * Get the formatted colour text based on the colour space and opacity.
 */
export const getSimplifiedCSSColorText = (
    color: string,
    colorSpace: CssColorSpace = 'hex',
    alpha: number = 1,
): string => {
    const chromaColor = getChromaColorSafe(color);
    return colorSpace === 'hex'
        ? chromaColor.alpha(alpha).hex()
        : simplifiedColorString(chromaColor.alpha(alpha).css(colorSpace));
};
